import React, { useCallback, useEffect, useRef, useState } from 'react';
import echo from '../../services/echo';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  acceptSurplus,
  EMPTY_FILTERS,
  errorMessage,
  fetchLabelsToPrint,
  fetchRows,
  fetchSurplus,
  FilterOptions,
  LabelRequest,
  markLabelsPrinted,
  PackingFilters,
  PackingPage,
  PackingRow,
  saveOverdueDays,
  setFlags,
} from './packingApi';
import PackingActionModal, { PackingAction } from './PackingActionModal';
import PackingDefectReasonsView from './PackingDefectReasonsView';
import PackingDetailsModal from './PackingDetailsModal';
import PackingFiltersBar from './PackingFiltersBar';
import PackingHistoryView from './PackingHistoryView';
import PackingInstructionsView from './PackingInstructionsView';
import PackingLabelsView from './PackingLabelsView';
import PackingRowsTable from './PackingRowsTable';
import PackingSurplusView from './PackingSurplusView';
import { ACCENT, btn, card, input } from './packingStyles';
import ManualSurplusModal from '../surplus/ManualSurplusModal';
import { addPackingSurplus, removePackingSurplus } from '../surplus/surplusApi';

type Tab = 'open' | 'overdue' | 'surplus' | 'labels' | 'history' | 'reasons' | 'instructions';

const POLL_MS = 30_000;

/** Site / line / week chosen at this computer — Pęcice and Sochaczew each open on their own lines. */
const FILTERS_KEY = 'packing.filters';

const loadFilters = (): PackingFilters => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(FILTERS_KEY) ?? 'null');
    return saved && typeof saved === 'object' ? { ...EMPTY_FILTERS, ...saved } : EMPTY_FILTERS;
  } catch {
    return EMPTY_FILTERS;
  }
};

const storeFilters = (filters: PackingFilters) => {
  try {
    window.localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  } catch {
    // Storage blocked (private window): the choice just is not remembered.
  }
};

/**
 * Packing station ("Stacja pakowania"): what really arrived from the lines (operator-entered pieces,
 * pieces rewound for big-spool lines), packed by typing a quantity. Sub-tabs: overdue rows, surplus,
 * labels to print (Andrzej), what was handed to the warehouse and — for the manager — defect reasons
 * and packing instructions. The site / line / week filters split the work between the sites.
 */
const PackingStation: React.FC = () => {
  const { t } = useLanguage();
  const { isAdmin, isManager, can } = useAuth();
  const [tab, setTab] = useState<Tab>('open');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<PackingPage | null>(null);
  const [surplus, setSurplus] = useState<LabelRequest[]>([]);
  const [labels, setLabels] = useState<LabelRequest[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [action, setAction] = useState<{ action: PackingAction; row: PackingRow } | null>(null);
  const [details, setDetails] = useState<number | null>(null);
  const [addingSurplus, setAddingSurplus] = useState(false);
  const [message, setMessage] = useState<{ text: string; error?: boolean } | null>(null);
  const [overdueDays, setOverdueDays] = useState('');
  const [filters, setFilters] = useState<PackingFilters>(loadFilters);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const canLabels = can('packing_labels');

  // Debounced search: one request after typing (or a scanner) stops.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(id);
  }, [search]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Live updates: a burst of events (a line finishing many positions) causes one reload, and the
  // fallback polling runs only while the tab is visible.
  useEffect(() => {
    let pending: number | undefined;
    const coalesced = () => {
      window.clearTimeout(pending);
      pending = window.setTimeout(refresh, 800);
    };
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    const channel = echo.channel('packaging');
    channel.listen('.updated', coalesced);
    return () => {
      window.clearInterval(id);
      window.clearTimeout(pending);
      document.removeEventListener('visibilitychange', onVisible);
      echo.leaveChannel('packaging');
    };
  }, [refresh]);

  const flash = useCallback((text: string, error = false) => {
    setMessage({ text, error });
    window.setTimeout(() => setMessage(null), 3500);
  }, []);

  const changeFilters = (next: PackingFilters) => {
    setFilters(next);
    setPage(1);
    storeFilters(next);
  };

  useEffect(() => {
    // Filters, polling and live events can overlap: only the newest request may fill the screen.
    let current = true;
    const load = async () => {
      try {
        if (tab === 'open' || tab === 'overdue') {
          const result = await fetchRows(tab, query, filters, page);
          if (!current) return;
          setRows(result);
          setFilterOptions(result.meta.filters);
          setOverdueDays((current) => current || String(result.meta.overdue_days));
        } else if (tab === 'surplus') {
          const result = await fetchSurplus();
          if (current) setSurplus(result);
        } else if (tab === 'labels') {
          const result = await fetchLabelsToPrint();
          if (current) setLabels(result);
        }
      } catch (e: any) {
        if (current) flash(errorMessage(e, t('packing.errorLoad')), true);
      }
    };
    void load();
    return () => {
      current = false;
    };
  }, [tab, query, filters, page, refreshKey, flash, t]);

  const run = async (call: () => Promise<unknown>) => {
    try {
      await call();
      flash(t('packing.saved'));
      refresh();
    } catch (e: any) {
      flash(errorMessage(e, t('packing.errorSave')), true);
    }
  };

  const tabs: [Tab, string, number | null][] = [
    ['open', t('packing.tabs.open'), rows?.meta.counts.open ?? null],
    ['overdue', t('packing.tabs.overdue'), rows?.meta.counts.overdue ?? null],
    ['surplus', t('packing.tabs.surplus'), null],
    ...(canLabels ? ([['labels', t('packing.tabs.labels'), null]] as [Tab, string, null][]) : []),
    ['history', t('packing.tabs.history'), null],
    ...(isManager
      ? ([
          ['reasons', t('packing.tabs.reasons'), null],
          ['instructions', t('packing.tabs.instructions'), null],
        ] as [Tab, string, null][])
      : []),
  ];
  const listTab = tab === 'open' || tab === 'overdue';
  const filtered = listTab || tab === 'history';

  return (
    <div style={{ padding: 'clamp(8px, 2vw, 20px)', maxWidth: 1800, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <h1 style={{ margin: 0, fontSize: 26, color: '#0f172a' }}>{t('packing.title')}</h1>
        <span style={{ color: '#64748b', fontSize: 14 }}>{t('packing.subtitle')}</span>
      </div>

      <div role="tablist" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {tabs.map(([key, label, count]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            type="button"
            onClick={() => { setTab(key); setPage(1); }}
            style={{
              ...btn(tab === key ? 'primary' : 'ghost'),
              ...(key === 'overdue' && (count ?? 0) > 0 && tab !== key ? { borderColor: '#dc2626', color: '#b91c1c' } : {}),
            }}
          >
            {label}
            {count !== null && (
              <span style={{ marginLeft: 8, padding: '1px 8px', borderRadius: 999, fontSize: 12, background: tab === key ? '#fff' : key === 'overdue' && count > 0 ? '#dc2626' : '#e2e8f0', color: tab === key ? ACCENT : key === 'overdue' && count > 0 ? '#fff' : '#334155' }}>
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {message && (
        <div role="status" style={{ position: 'fixed', top: 16, right: 16, zIndex: 1100, padding: '10px 16px', borderRadius: 8, fontWeight: 600, color: '#fff', background: message.error ? '#dc2626' : '#16a34a', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {message.text}
        </div>
      )}

      <div style={{ ...card, padding: 'clamp(10px, 2vw, 16px)' }}>
        {filtered && (
          <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #f1f5f9' }}>
            <PackingFiltersBar value={filters} options={filterOptions} onChange={changeFilters} />
          </div>
        )}
        {listTab && (
          <>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
              <input
                ref={searchRef}
                type="search"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('packing.searchPlaceholder')}
                aria-label={t('packing.searchPlaceholder')}
                style={{ ...input, flex: '1 1 320px', fontSize: 17 }}
              />
              {tab === 'overdue' && isManager && (
                <form
                  style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}
                  onSubmit={(e) => { e.preventDefault(); void run(() => saveOverdueDays(parseInt(overdueDays, 10))); }}
                >
                  <label style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                    {t('packing.overdue.days')}
                    <input type="number" min={1} max={90} value={overdueDays} onChange={(e) => setOverdueDays(e.target.value)} style={{ ...input, width: 110, marginTop: 4 }} />
                  </label>
                  <button type="submit" style={btn('ghost')}>{t('packing.modal.save')}</button>
                </form>
              )}
            </div>
            {tab === 'overdue' && rows && (
              <p style={{ margin: '0 0 12px', color: '#b91c1c', fontWeight: 600, fontSize: 14 }}>
                {t('packing.overdue.hint', { days: rows.meta.overdue_days })}
              </p>
            )}
            {rows && rows.data.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 16 }}>
                {query ? t('packing.emptySearch', { q: query }) : t('packing.empty')}
              </div>
            ) : (
              rows && (
                <PackingRowsTable
                  rows={rows.data}
                  isManager={isManager}
                  onAction={(a, row) => setAction({ action: a, row })}
                  onToggleCartons={(row) => void run(() => setFlags(row.pivot_id, { no_cartons: !row.no_cartons }))}
                  onClearLabels={(row) => void run(() => setFlags(row.pivot_id, { labels_missing_qty: null }))}
                  onDetails={(row) => setDetails(row.pivot_id)}
                />
              )
            )}
            {rows && rows.meta.last_page > 1 && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginTop: 12 }}>
                <button type="button" style={btn('ghost', true)} disabled={page <= 1} onClick={() => setPage(page - 1)}>{t('packing.prev')}</button>
                <span style={{ fontSize: 14 }}>{t('packing.page', { page, last: rows.meta.last_page })}</span>
                <button type="button" style={btn('ghost', true)} disabled={page >= rows.meta.last_page} onClick={() => setPage(page + 1)}>{t('packing.next')}</button>
              </div>
            )}
          </>
        )}
        {tab === 'surplus' && (
          <PackingSurplusView
            requests={surplus}
            onAccept={(r) => void run(() => acceptSurplus(r.id))}
            onAdd={isAdmin ? () => setAddingSurplus(true) : undefined}
            onRemove={
              isAdmin
                ? (r) => {
                    if (window.confirm(t('manualSurplus.confirmRemove'))) void run(() => removePackingSurplus(r.id));
                  }
                : undefined
            }
          />
        )}
        {tab === 'labels' && canLabels && <PackingLabelsView requests={labels} onPrinted={(r) => void run(() => markLabelsPrinted(r.id))} />}
        {tab === 'history' && <PackingHistoryView refreshKey={refreshKey} filters={filters} onOptions={setFilterOptions} />}
        {tab === 'reasons' && isManager && <PackingDefectReasonsView onMessage={flash} />}
        {tab === 'instructions' && isManager && <PackingInstructionsView onMessage={flash} />}
      </div>

      {action && (
        <PackingActionModal
          action={action.action}
          row={action.row}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            flash(t('packing.saved'));
            refresh();
            searchRef.current?.focus();
          }}
        />
      )}
      {addingSurplus && (
        <ManualSurplusModal
          title={t('manualSurplus.packingTitle')}
          onClose={() => setAddingSurplus(false)}
          onSave={async (index, quantity) => {
            await addPackingSurplus(index, quantity);
            setAddingSurplus(false);
            flash(t('packing.saved'));
            refresh();
          }}
        />
      )}
      {details !== null && (
        <PackingDetailsModal pivotId={details} canDelete={isManager} onClose={() => setDetails(null)} onChanged={refresh} />
      )}
    </div>
  );
};

export default PackingStation;
